import { useContent } from 'contexts/Content/Content';
import { useStaging } from 'contexts/Staging/Staging';
import { getUrl } from 'helpers';
import Head from 'next/head';
import { useRouter } from 'next/router';

const INDEXABLE_ROBOTS = 'index,follow,max-image-preview:large';
const NOINDEX_ROBOTS = 'noindex,nofollow,noarchive';
const DEFAULT_FEATURE_LIST = [
    'Interactive portfolio management game built around an ant colony scenario',
    'Strategic resource allocation across core, adjacent, and disruptive bets',
    'Environmental risk simulation for portfolio resilience',
    'Business agility and corporate governance learning experience',
];

export default function DocHead() {
    const content = useContent();
    const router = useRouter();
    const isStaging = useStaging();

    const {
        canonicalUrl,
        documentDescription,
        documentTitle,
        favicon,
        faq,
        siteName,
        socialDescription,
        socialImage,
        socialImageAlt,
        socialTitle,
    } = content.general;

    const routeMetadata = {
        '/': {
            canonicalUrl,
            documentDescription,
            documentTitle,
            faq,
            includeStructuredData: true,
            robots: INDEXABLE_ROBOTS,
            socialDescription,
            socialTitle,
            structuredDataName: 'Portfolio Management Game',
        },
        '/game': {
            canonicalUrl: `${canonicalUrl}/game`,
            documentDescription:
                "Play Kromatic's interactive portfolio management game and adjust your strategy as competition and environmental risk change.",
            documentTitle: 'Play Portfolio Management Game - Kromatic',
            faq: [] as SeoFaqEntry[],
            includeStructuredData: false,
            robots: INDEXABLE_ROBOTS,
            socialDescription:
                "Play Kromatic's interactive portfolio management game and adjust your strategy as competition and environmental risk change.",
            socialTitle: 'Play Portfolio Management Game | Kromatic',
            structuredDataName: 'Portfolio Management Game',
        },
        '/simulation': {
            canonicalUrl: `${canonicalUrl}/simulation`,
            documentDescription:
                'Adjust core, adjacent, and disruptive allocations to see how portfolio choices change bankruptcy risk as the environment becomes more volatile.',
            documentTitle: 'Portfolio Management Simulation - Kromatic',
            faq: [] as SeoFaqEntry[],
            includeStructuredData: false,
            robots: INDEXABLE_ROBOTS,
            socialDescription:
                'Adjust core, adjacent, and disruptive allocations to explore how environmental risk changes portfolio outcomes.',
            socialTitle: 'Portfolio Management Simulation | Kromatic',
            structuredDataName: 'Portfolio Management Simulation',
        },
    };
    const activeRoute = routeMetadata[router.pathname as keyof typeof routeMetadata] || routeMetadata['/'];
    const effectiveRobots = isStaging ? NOINDEX_ROBOTS : activeRoute.robots;
    const shareDescription = activeRoute.socialDescription || socialDescription || activeRoute.documentDescription;
    const shareTitle = activeRoute.socialTitle || socialTitle || activeRoute.documentTitle;
    const webApplicationStructuredData = activeRoute.includeStructuredData
        ? {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: activeRoute.structuredDataName,
            applicationCategory: 'BusinessApplication',
            description: activeRoute.documentDescription,
            featureList: DEFAULT_FEATURE_LIST,
            isAccessibleForFree: true,
            offers: {
                '@type': 'Offer',
                price: 0,
                priceCurrency: 'USD',
            },
            operatingSystem: 'Any',
            publisher: {
                '@type': 'Organization',
                name: siteName,
                url: 'https://kromatic.com',
            },
            url: activeRoute.canonicalUrl,
        }
        : null;
    const faqStructuredData =
        activeRoute.includeStructuredData && activeRoute.faq.length > 0
            ? {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: activeRoute.faq.map(entry => ({
                    '@type': 'Question',
                    name: entry.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: entry.answer,
                    },
                })),
            }
            : null;

    return (
        <Head>
            <title>{activeRoute.documentTitle}</title>
            <meta name="description" content={activeRoute.documentDescription} />
            <meta name="robots" content={effectiveRobots} />
            <meta name="googlebot" content={effectiveRobots} />
            <link rel="canonical" href={activeRoute.canonicalUrl} />
            <link rel="shortcut icon" href={getUrl(favicon)} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:title" content={shareTitle} />
            <meta property="og:description" content={shareDescription} />
            <meta property="og:url" content={activeRoute.canonicalUrl} />
            <meta property="og:image" content={socialImage} />
            <meta property="og:image:alt" content={socialImageAlt} />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:width" content="1394" />
            <meta property="og:image:height" content="880" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={shareTitle} />
            <meta name="twitter:description" content={shareDescription} />
            <meta name="twitter:image" content={socialImage} />
            <meta name="twitter:image:alt" content={socialImageAlt} />
            {webApplicationStructuredData && (
                <script
                    key="webapplication-structured-data"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(webApplicationStructuredData) }}
                />
            )}
            {faqStructuredData && (
                <script
                    key="faq-structured-data"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
                />
            )}
        </Head>
    );
}
